import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError, take, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';

export interface WhitelistDto {
  permissionCode: string;
  resourceType: string;
  resourceValue: string;
}

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  // 記憶體快取：Key 為 "PermissionCode_ResourceType", Value 為 ResourceValue 陣列
  private readonly whitelistCache = new Map<string, string[]>();
  private isLoaded = false;

  // Lazy Load 機制
  private whitelistLoaded$ = new ReplaySubject<void>(1);
  private loadingInProgress = false;

  constructor(private readonly http: HttpClient) {}

  /**
   * 是否已載入白名單（供外部檢查，主要流程應透過 ensureLoaded()）
   */
  public get loaded(): boolean {
    return this.isLoaded;
  }

  /**
   * 冪等 Lazy Load：確保白名單已載入
   * - 若已載入直接回傳
   * - 防止重複請求：多個元件同時呼叫只發一次 API
   * - 失敗也釋放信號，避免 UI 卡死
   */
  public ensureLoaded(): Observable<void> {
    if (this.isLoaded) {
      return of(undefined);
    }
    if (!this.loadingInProgress) {
      this.loadingInProgress = true;
      this.loadUserWhitelists().subscribe({
        next: () => this.whitelistLoaded$.next(),
        error: () => this.whitelistLoaded$.next() // 失敗也要釋放，避免 UI 卡死
      });
    }
    return this.whitelistLoaded$.asObservable().pipe(take(1));
  }

  /**
   * 在系統初始化 (APP_INITIALIZER) 或登入成功時呼叫此方法
   * 負責撈取該使用者的所有白名單，並重組為 Map 快取
   */
  public loadUserWhitelists(): Observable<WhitelistDto[]> {
    return this.http.get<WhitelistDto[]>(`${environment.logisticsApiUrl}/permissions/whitelists/`).pipe(
      tap((data: WhitelistDto[]) => {
        this.whitelistCache.clear(); // 清除舊快取

        data.forEach(item => {
          const key = `${item.permissionCode}_${item.resourceType}`;
          let values = this.whitelistCache.get(key);

          if (!values) {
            values = [];
            this.whitelistCache.set(key, values);
          }

          values.push(item.resourceValue);
        });

        this.isLoaded = true;
      }),
      catchError((err) => {
        console.error('白名單載入失敗，將退回基本權限 (Graceful Degradation)', err);
        this.isLoaded = true; // 即使失敗也標記為已載入，避免 UI 卡死
        return of([]); // 靜默處理回傳空陣列
      })
    );
  }

  /**
   * 供前端共用元件呼叫的同步查詢方法
   */
  public getWhitelistValues(permissionCode?: PermissionConstants | string, resourceType?: string): string[] {
    if (!this.isLoaded) {
      console.warn('警告：白名單尚未載入，請確認是否已在登入流程呼叫 loadUserWhitelists');
    }

    // 若沒有傳入條件，直接回傳空陣列
    if (!permissionCode || !resourceType) {
      return [];
    }

    const key = `${permissionCode}_${resourceType}`;
    return this.whitelistCache.get(key) || [];
  }

  /**
   * 使用者登出時呼叫，清空記憶體避免資料外洩
   */
  public clearCache(): void {
    this.whitelistCache.clear();
    this.isLoaded = false;
    this.loadingInProgress = false;
  }
}
